import type { ReactNode } from "react";

import type { ClubView } from "../game/types";
import { Crest, FormChips, Money, MoraleFace, Panel } from "../ui/atoms";
import { BotaoDoClube } from "./FichaDoClube";
import { numero, rotuloDeFase, useT } from "../i18n";
import { tabelaDaCompeticao } from "./CompetitionsPanel";
import { RivalsCard } from "./panels";

/**
 * A coluna da direita do elenco, quando nenhum jogador está marcado.
 *
 * O que se olha a cada rodada sem sair do elenco: o próximo jogo, a campanha,
 * o caixa e os rivais. Clicar num jogador troca esta coluna pela ficha dele, e
 * desmarcar traz de volta; a regra mora em `ClubHub`.
 */
export function ColunaDoAdm({
  view,
  escudo,
  aoAbrirClube,
}: {
  view: ClubView;
  /** O escudo do próprio clube, com cosmético e desenho, do jeito que o hub monta. */
  escudo: (tamanho: number) => ReactNode;
  aoAbrirClube: (clubId: string) => void;
}): ReactNode {
  return (
    <aside className="coluna-do-adm">
      <ProximoJogo view={view} escudo={escudo} aoAbrirClube={aoAbrirClube} />
      <Desempenho view={view} />
      <Financas view={view} />
      {view.rivals.length > 0 && <RivalsCard view={view} />}
    </aside>
  );
}

function ProximoJogo({
  view,
  escudo,
  aoAbrirClube,
}: {
  view: ClubView;
  escudo: (tamanho: number) => ReactNode;
  aoAbrirClube: (clubId: string) => void;
}): ReactNode {
  const t = useT();
  const next = view.nextMatch;
  return (
    <Panel title={t("adm.proximoJogo")}>
      {!next || view.seasonOver ? (
        <p className="muted" style={{ margin: 0, fontSize: 12 }}>
          {view.seasonOver ? t("hub.temporadaEncerrada") : t("hub.semJogoMarcado")}
        </p>
      ) : (
        <div className="adm-confronto">
          <div className="adm-escudos">
            {escudo(36)}
            <span className="label-muted">{t("hub.versus")}</span>
            <BotaoDoClube clubId={next.opponentId} nome={next.opponent} aoAbrir={aoAbrirClube}>
              <Crest colors={next.opponentColors} short={next.opponentShort} size={36} />
            </BotaoDoClube>
          </div>
          <div className="adm-confronto-texto">
            <BotaoDoClube clubId={next.opponentId} nome={next.opponent} aoAbrir={aoAbrirClube}>
              <strong>{next.opponent}</strong>
            </BotaoDoClube>
            <span className="muted">
              {next.competition}
              {next.stage === "liga" || next.stage === "estadual"
                ? t("hub.competicaoRodada", { rodada: next.matchday ?? next.round })
                : t("hub.competicaoFase", { fase: rotuloDeFase(next.stage) })}
            </span>
            <span className="muted">
              {next.home
                ? `${t("hub.emCasa")} · ${t("hub.lugares", { capacidade: numero(view.club.stadiumCapacity) })}`
                : t("hub.foraDeCasa")}
            </span>
            {next.derby && <b className="rodape-classico">{t("hub.classico")}</b>}
          </div>
        </div>
      )}
    </Panel>
  );
}

/** A campanha na competição do próximo jogo: o estadual no começo do ano, a liga depois. */
function Desempenho({ view }: { view: ClubView }): ReactNode {
  const t = useT();
  const { nome, linhas } = tabelaDaCompeticao(view, view.nextMatch?.competition);
  const minha = linhas.find((linha) => linha.isMine);
  const numeros: Array<[string, number]> = [
    [t("adm.jogos"), minha?.played ?? 0],
    [t("adm.vitorias"), minha?.wins ?? 0],
    [t("adm.empates"), minha?.draws ?? 0],
    [t("adm.derrotas"), minha?.losses ?? 0],
  ];
  return (
    <Panel title={t("adm.desempenho")}>
      <div className="adm-campanha">
        <div>
          <div className="adm-posicao num">
            {minha && minha.played > 0 ? t("hub.posicaoValor", { posicao: minha.position }) : t("comum.vazio")}
          </div>
          <div className="label-muted">{nome}</div>
        </div>
        <div className="adm-pontos">
          <span className="num">{minha?.points ?? 0}</span>
          <span className="label-muted">{t("adm.pontos")}</span>
        </div>
      </div>
      <div className="adm-numeros">
        {numeros.map(([rotulo, valor]) => (
          <div key={rotulo}>
            <span className="label-muted">{rotulo}</span>
            <b className="num">{valor}</b>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 10 }}>
        <FormChips form={view.club.form} />
      </div>
    </Panel>
  );
}

function Financas({ view }: { view: ClubView }): ReactNode {
  const t = useT();
  const { finance, club } = view;
  const linhas: Array<[string, number | null]> = [
    [t("financas.saldoEmCaixa"), club.cash],
    [t("financas.receitaPorRodada"), finance.incomePerRound],
    [t("financas.despesaPorRodada"), finance.expensePerRound],
    [t("financas.folha"), finance.wagesPerRound],
  ];
  return (
    <Panel title={t("adm.financas")}>
      <div className="adm-pares">
        {linhas.map(([rotulo, valor]) => (
          <div key={rotulo}>
            <span className="muted">{rotulo}</span>
            {valor === null ? <span className="muted">{t("comum.vazio")}</span> : <Money value={valor} />}
          </div>
        ))}
        <div>
          <span className="muted">{t("adm.moralDoClube")}</span>
          <span className="row" style={{ gap: 5 }}>
            <MoraleFace value={club.moraleAverage} size={14} />
            <span className="num">{club.moraleAverage}</span>
          </span>
        </div>
      </div>
    </Panel>
  );
}
